import { Document, pdf } from '@react-pdf/renderer';
import middleware from '../Api/Middleware';
import InvoicePDFPages from './InvoicePdf';
import { formatCurrency, getCountryName, parseCurrency } from "../../utils";
import ToastPromise from '../Toasts/ToastPromise';

const calculateOrderDiscount = (order) => {
    if (!order.sale_amount) return 0;
    
    if (order.sale_unit === 0) {
        const discountAmount = parseCurrency(order.sale_amount);
        const originalPrice = (parseCurrency(order.total_price_incl_tax) - parseCurrency(order.delivery_cost)) + discountAmount;
        return originalPrice > 0 ? (discountAmount / originalPrice) * 100 : 0;
    } else {
        const discountPercentage = parseCurrency(order.sale_amount);
        return Math.min(100, Math.max(0, discountPercentage));
    }
};

const processInvoiceLines = (invoiceLines, order, includeDeliveryCost = true) => {
    const lines = [];
    let subtotal = 0;
    let hasItems = false;

    const orderDiscountPercentage = calculateOrderDiscount(order);

    invoiceLines.forEach(line => {
        const product = line.sold_product;
        if (!product) return;

        let itemPrice = parseCurrency(product.sale_price_incl_tax ?? line.price_incl_tax);

        if (orderDiscountPercentage > 0) {
            itemPrice = itemPrice * (1 - orderDiscountPercentage / 100);
        }

        const total = itemPrice * line.quantity;
        subtotal += total;
        hasItems = true;

        lines.push({
            quantity: line.quantity,
            name: product.product?.name || product.product_name,
            description: product.product?.description || product.product_description,
            unitPrice: formatCurrency(itemPrice),
            originalPrice: orderDiscountPercentage > 0 ? formatCurrency(parseCurrency(line.price_incl_tax)) : null,
            showOriginalPrice: orderDiscountPercentage > 0,
            totalPrice: formatCurrency(total)
        });
    });

    // Alleen verzendkosten toevoegen als includeDeliveryCost true is
    const deliveryCost = parseCurrency(order.delivery_cost);
    if (deliveryCost > 0 && hasItems && includeDeliveryCost) {
        subtotal += deliveryCost;
        lines.push({
            quantity: 1,
            name: 'Verzendkosten',
            description: null,
            unitPrice: formatCurrency(deliveryCost),
            originalPrice: null,
            showOriginalPrice: false,
            totalPrice: formatCurrency(deliveryCost)
        });
    }

    ['note', 'reference'].forEach(field => { 
        if (order[field]) {
            lines.push({
                quantity: null,
                name: field === 'note' ? 'Opmerking' : 'Referentie',
                description: order[field],
                unitPrice: null,
                originalPrice: null,
                showOriginalPrice: false,
                totalPrice: null
            });
        }
    });

    return { lines, subtotal, hasItems };
};

const calculateTotals = (subtotal, vatRate = 0.21) => {
    const totalExclVat = subtotal / (1 + vatRate);
    const vat = subtotal - totalExclVat;

    return {
        subtotal: formatCurrency(subtotal),
        totalExclBtw: formatCurrency(totalExclVat),
        btw: formatCurrency(vat),
        totalInclBtw: formatCurrency(subtotal)
    };
};

const formatInvoiceData = (invoiceData, company, includeDeliveryCost = true) => {
    const { invoice, order } = invoiceData;
    const customer = order.client_consumer ?? order.client_business;

    const { lines: productLines, subtotal, hasItems } = processInvoiceLines(invoiceData.invoice_lines, order, includeDeliveryCost);

    if (!hasItems) return null;

    const vatRate = company.vat_rate || order.vat_rate || 0.21;

    const invoiceDate = new Date(invoice.billed_on);
    const dueDate = new Date(invoiceDate);
    dueDate.setDate(invoiceDate.getDate() + company.payment_term);

    const footerText = `Te betalen binnen ${company.payment_term} dagen (voor ${dueDate.toLocaleDateString('nl-NL')}) op rekeningnummer ${company.iban} t.n.v. ${company.company_name} onder vermelding van klantnummer en factuurnummer`;

    return {
        documentNumber: invoice.invoice_number,
        documentDate: invoiceDate.toLocaleDateString('nl-NL'),
        customer,
        company,
        deliveryAddress: invoiceData.delivery_address ? {
            ...invoiceData.delivery_address,
            country: getCountryName(invoiceData.delivery_address?.country?.country_code_2)
        } : null,
        invoiceAddress: invoiceData.invoice_address ? {
            ...invoiceData.invoice_address,
            country: getCountryName(invoiceData.invoice_address?.country?.country_code_2)
        } : null,
        productLines,
        totals: calculateTotals(subtotal, vatRate),
        footerText,
    };
};

/**
 * Convert PDF blob to base64 string
 */
const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => {
            const commaIndex = reader.result.indexOf(',');
            resolve(reader.result.substring(commaIndex + 1));
        };
        reader.onerror = (error) => reject(error);
    });

/**
 * Upload invoice PDF to backend
 * @param {number} invoiceId - The invoice ID
 * @param {Blob} pdfBlob - The PDF blob to upload
 * @param {string} filename - Optional filename
 */
export const uploadInvoicePdf = async (invoiceId, pdfBlob, filename = 'factuur.pdf') => {
    try {
        const base64String = await blobToBase64(pdfBlob);

        const response = await middleware.post(`invoices/${invoiceId}/pdf`, {
            pdf_base64: base64String,
            filename: filename
        });

        return response.data;
    } catch (error) {
        console.error('Error uploading invoice PDF:', error);
        throw error;
    }
};

const handleFileOperations = async (pdfBlob, filename, print, download) => {
    const url = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));

    if (print) {
        const printWindow = window.open(url, '_blank');
        printWindow?.addEventListener('load', () => printWindow.print());
    }

    if (download) {
        const link = document.createElement('a');
        Object.assign(link, { href: url, download: filename, target: '_blank' });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const generateInvoice = async (orderIds, print = false, download = false, force_new = false, uploadToBackend = true) => {
    return await ToastPromise({
        promise: (async () => {
            if (!Array.isArray(orderIds) || orderIds.length === 0) {
                const e = new Error('Geen order ID\'s meegegeven');
                e.code = 'no_order_ids';
                throw e;
            }

            const response = await middleware.get('orders/generateInvoices', {
                params: {
                    orders: orderIds.map(id => ({ order_id: id })),
                    allow_partial: 1,
                    force_new: force_new ? 1 : 0
                },
            });

            const { orders, company_info: company } = response.data.data;

            if (!window.Buffer) {
                const { Buffer } = await import('buffer');
                window.Buffer = Buffer;
            }

            const counters = { success: 0, failed: 0, notProcessed: 0, uploaded: 0 };
            const formattedOrders = [];
            const invoiceDataMap = new Map(); // Map to store invoice_id -> invoiceData

            // Check voor elke order welke invoice_id's al bestaan
            const existingInvoicesPerOrder = new Map();
            const uniqueOrderIds = [...new Set(orders
                .filter(invoiceData => !invoiceData.code && invoiceData.order?.order_id)
                .map(invoiceData => invoiceData.order.order_id))];

            // Haal alle bestaande invoices op voor deze orders
            for (const orderId of uniqueOrderIds) {
                try {
                    const invoicesResponse = await middleware.get(`invoices?order_id=${orderId}`);
                    const existingInvoices = invoicesResponse.data.data || [];
                    existingInvoicesPerOrder.set(orderId, existingInvoices);
                } catch (error) {
                    console.error(`Failed to fetch existing invoices for order ${orderId}:`, error);
                    existingInvoicesPerOrder.set(orderId, []);
                }
            }

            // Groepeer invoices per order_id en sorteer op invoice_id (laagste = oudste = eerste)
            const invoicesByOrder = new Map();
            for (const invoiceData of orders) {
                if (invoiceData.code) {
                    counters[invoiceData.code === 'needs_processing' ? 'notProcessed' : 'failed']++;
                    continue;
                }

                const orderId = invoiceData.order?.order_id;
                if (!orderId) {
                    counters.failed++;
                    continue;
                }

                if (!invoicesByOrder.has(orderId)) {
                    invoicesByOrder.set(orderId, []);
                }
                invoicesByOrder.get(orderId).push(invoiceData);
            }

            // Sorteer invoices per order op billed_on datum (oudste eerst)
            for (const [orderId, invoiceList] of invoicesByOrder.entries()) {
                invoiceList.sort((firstInvoice, secondInvoice) => {
                    // Eerst sorteren op datum
                    const firstDate = new Date(firstInvoice.invoice?.billed_on || 0);
                    const secondDate = new Date(secondInvoice.invoice?.billed_on || 0);
                    if (firstDate.getTime() !== secondDate.getTime()) {
                        return firstDate - secondDate;
                    }
                    // Als datum gelijk is, sorteer op invoice_id
                    const firstInvoiceId = firstInvoice.invoice?.invoice_id || 0;
                    const secondInvoiceId = secondInvoice.invoice?.invoice_id || 0;
                    return firstInvoiceId - secondInvoiceId;
                });

            }

            // Verwerk alle invoices en markeer alleen de eerste per order voor verzendkosten
            for (const [orderId, invoiceList] of invoicesByOrder.entries()) {
                // Haal alle bestaande invoices op voor deze order (inclusief oude)
                const allInvoicesForOrder = existingInvoicesPerOrder.get(orderId) || [];

                // Sorteer ALLE invoices (oude + nieuwe) op datum en invoice_id
                const allInvoiceIds = allInvoicesForOrder.map(existingInvoice => ({
                    invoice_id: existingInvoice.invoice_id,
                    billed_on: existingInvoice.billed_on
                }));

                // Voeg de huidige invoices toe aan de lijst
                invoiceList.forEach(currentInvoice => {
                    if (currentInvoice.invoice?.invoice_id) {
                        allInvoiceIds.push({
                            invoice_id: currentInvoice.invoice.invoice_id,
                            billed_on: currentInvoice.invoice.billed_on
                        });
                    }
                });

                // Sorteer alle invoices
                allInvoiceIds.sort((firstInvoice, secondInvoice) => {
                    const firstDate = new Date(firstInvoice.billed_on || 0);
                    const secondDate = new Date(secondInvoice.billed_on || 0);
                    if (firstDate.getTime() !== secondDate.getTime()) {
                        return firstDate - secondDate;
                    }
                    return firstInvoice.invoice_id - secondInvoice.invoice_id;
                });

                const firstInvoiceId = allInvoiceIds[0]?.invoice_id;

                for (let invoiceIndex = 0; invoiceIndex < invoiceList.length; invoiceIndex++) {
                    const invoiceData = invoiceList[invoiceIndex];
                    const currentInvoiceId = invoiceData.invoice?.invoice_id;

                    // Alleen de allereerste invoice (vergeleken met ALLE invoices) krijgt verzendkosten
                    const isFirstInvoiceForOrder = (currentInvoiceId === firstInvoiceId);


                    const formattedInvoiceData = formatInvoiceData(invoiceData, company, isFirstInvoiceForOrder);
                    if (!formattedInvoiceData) {
                        counters.notProcessed++;
                        continue;
                    }

                    formattedOrders.push(formattedInvoiceData);
                    // Store invoice_id for later upload
                    if (invoiceData.invoice?.invoice_id) {
                        invoiceDataMap.set(formattedInvoiceData.documentNumber, invoiceData.invoice.invoice_id);
                    }
                    counters.success++;
                }
            }

            if (formattedOrders.length === 0) {
                throw new Error(`Geen facturen konden worden gegenereerd. ${counters.notProcessed} orders nog niet verwerkt, ${counters.failed} mislukt.`);
            }

            // Generate PDFs for each invoice individually if uploadToBackend is true
            if (uploadToBackend && formattedOrders.length > 0) {
                for (const formattedOrder of formattedOrders) {
                    try {
                        const pdfDoc = (
                            <Document>
                                <InvoicePDFPages
                                    documentType="FACTUUR"
                                    {...formattedOrder}
                                />
                            </Document>
                        );

                        const pdfBlob = await pdf(pdfDoc).toBlob();
                        const invoiceId = invoiceDataMap.get(formattedOrder.documentNumber);

                        if (invoiceId) {
                            const filename = `Factuur_${formattedOrder.documentNumber}.pdf`;
                            await uploadInvoicePdf(invoiceId, pdfBlob, filename);
                            counters.uploaded++;
                        }
                    } catch (uploadError) {
                        console.error(`Failed to upload PDF for invoice ${formattedOrder.documentNumber}:`, uploadError);
                        // Continue with other invoices even if one fails
                    }
                }
            }

            // Generate combined PDF for download/print
            const pdfDoc = (
                <Document>
                    {formattedOrders.map((order, index) => (
                        <InvoicePDFPages
                            key={index}
                            documentType="FACTUUR"
                            {...order}
                        />
                    ))}
                </Document>
            );

            const pdfBlob = await pdf(pdfDoc).toBlob();
            const filename = formattedOrders.length === 1
                ? `Factuur_${formattedOrders[0].documentNumber}.pdf`
                : `Facturen_${new Date().toLocaleDateString('nl-NL').replace(/\//g, '-')}.pdf`;

            await handleFileOperations(pdfBlob, filename, print, download);

            const { success, notProcessed, failed, uploaded } = counters;
            return {
                success: true,
                successCount: success,
                notProcessedCount: notProcessed,
                failedCount: failed,
                uploadedCount: uploaded,
                message: `${success} factuur(en) succesvol gegenereerd${notProcessed > 0 ? `, ${notProcessed} bestelling(en) nog niet verwerkt` : ''}${failed > 0 ? `, ${failed} mislukt` : ''}`
            };
        })(),
        pending: 'Facturen genereren…',
        successRenderer: (data) => data?.message || 'Facturen succesvol gegenereerd',
        errorRenderer: (error) =>
            error?.response?.data?.message ||
            error?.message ||
            'Er is een fout opgetreden bij het genereren van de facturen',
        autoClose: 5000,
    });
};

export default generateInvoice;